<script setup>
import { ref, computed, watch, onMounted, nextTick, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import * as z from 'zod'
import { toTypedSchema } from '@vee-validate/zod'
import { useForm } from 'vee-validate'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ProjectSelect } from '@/components/ui/project-select'
import { Spinner } from '@/components/ui/spinner'
import { ListFilter, X, Plus } from 'lucide-vue-next'
import { HOST, API } from '@/utils/http'
import { toast } from '@/components/ui/toast'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { useScreens } from 'vue-screen-utils'
import { formatTime } from '@/utils/datetime'
import { mergePolygons } from '@/utils/geometry'
import { FeatureSelection } from '@/components/feature-selection'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { VerifiedMarker } from '@/components/ui/verified-marker'

const props = defineProps({
  initialFilter: {
    type: Object,
    default: () => ({ filter: { type: 'AndFilter', config: [] } })
  },
  showFilterToggle: {
    type: Boolean,
    default: true
  },
  autoExpand: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  orgId: {
    type: [String, Number],
    required: false
  },
  hideProjectSelector: {
    type: Boolean,
    default: false
  },
  projectId: {
    type: [String, Number],
    default: null
  }
})

const emit = defineEmits(['update:filters'])

const store = useStore()
const orgProjects = ref([])
const isLoadingProjects = ref(false)
const allProjects = computed(() => store.getters['project/getFlatedProjects'])
const projects = computed(() => {
  if (orgProjects.value.length > 0) {
    return orgProjects.value
  }
  if (!props.orgId) {
    return allProjects.value
  }
  const orgIdNum = Number(props.orgId)
  return allProjects.value.filter(project =>
    project.organization_id === orgIdNum
  )
})

const isInitialized = ref(false)

const fetchOrgProjects = async () => {
  if (!props.orgId) return
  isLoadingProjects.value = true
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/org/${props.orgId}/project/all`
    })
    if (response.data && Array.isArray(response.data)) {
      orgProjects.value = response.data
    }
  } catch (error) {
    console.error('Failed to fetch org projects:', error)
  } finally {
    isLoadingProjects.value = false
  }
}
const settings = computed(() => store.getters['user/getUserSettings'] || { tz: 'UTC' })

const isLoadingTemplates = ref(false)
const isLoadingMembers = ref(false)
const templateOptions = ref([])
const memberOptions = ref([])
const selectedGeometries = ref([])
const selectedGeometryTypes = ref([])
const localSearchText = ref('')
let searchDebounceTimer = null
const geometryTypeOptions = [
  { id: 'Point', name: 'Point' },
  { id: 'Polygon', name: 'Polygon' },
  { id: 'LineString', name: 'LineString' },
  { id: 'MultiPolygon', name: 'MultiPolygon' }
]

const { mapCurrent } = useScreens({
  xs: '0px',
  sm: '640px',
  md: '768px',
  lg: '1024px',
});
const expanded = mapCurrent({ lg: false }, true)

const filterObject = ref({
  filter: {
    type: 'AndFilter',
    config: []
  }
})

const addProjectFilter = (projectIds) => {
  filterObject.value.filter.config = filterObject.value.filter.config.filter(
    f => !(f.type === 'ArrayFilter' && f.field_name === 'project_id')
  )

  if (projectIds && projectIds.length > 0) {
    filterObject.value.filter.config.push({
      type: 'ArrayFilter',
      field_name: 'project_id',
      config: projectIds
    })
  }
}

const addTemplateFilter = (templateIds) => {
  filterObject.value.filter.config = filterObject.value.filter.config.filter(
    f => !(f.type === 'ArrayFilter' && f.field_name === 'template_id')
  )

  if (templateIds && templateIds.length > 0) {
    filterObject.value.filter.config.push({
      type: 'ArrayFilter',
      field_name: 'template_id',
      config: templateIds
    })
  }
}

const addMemberFilter = (memberIds) => {
  filterObject.value.filter.config = filterObject.value.filter.config.filter(
    f => !(f.type === 'ArrayFilter' && f.field_name === 'member_id')
  )

  if (memberIds && memberIds.length > 0) {
    filterObject.value.filter.config.push({
      type: 'ArrayFilter',
      field_name: 'member_id',
      config: memberIds
    })
  }
}

const addDateRangeFilter = () => {
  filterObject.value.filter.config = filterObject.value.filter.config.filter(
    f => !(f.type === 'DateRangeFilter' && f.field_name === 'acquired')
  )
}

const addSearchFilter = (text) => {
  filterObject.value.filter.config = filterObject.value.filter.config.filter(
    f => !(f.type === 'TextFilter' && f.field_name === 'search')
  );

  if (text && text.trim()) {
    filterObject.value.filter.config.push({
      type: 'TextFilter',
      field_name: 'search',
      config: text
    });
  }
}

const addPublicDataFilter = (isPublic) => {
  filterObject.value.filter.config = filterObject.value.filter.config.filter(
    f => !(f.type === 'BoolFilter' && f.field_name === 'is_public')
  )

  filterObject.value.filter.config.push({
    type: 'BoolFilter',
    field_name: 'is_public',
    config: isPublic
  })
}

const addGeometryFilter = (geometries) => {
  console.log('addGeometryFilter', geometries)
  // Remove existing geometry filter
  filterObject.value.filter.config = filterObject.value.filter.config.filter(
    f => !(f.type === 'GeometryFilter' && f.field_name === 'geometry')
  )

  if (geometries && geometries.length > 0) {
    // Merge all selected geometries into MultiPolygon
    const mergedGeometry = mergePolygons(geometries)

    if (mergedGeometry) {
      filterObject.value.filter.config.push({
        type: 'GeometryFilter',
        field_name: 'geometry',
        config: mergedGeometry
      })
    }
  }
}

const addGeometryTypeFilter = (geometryTypes) => {
  filterObject.value.filter.config = filterObject.value.filter.config.filter(
    f => !(f.type === 'ArrayFilter' && f.field_name === 'geometry_type')
  )

  if (geometryTypes && geometryTypes.length > 0) {
    filterObject.value.filter.config.push({
      type: 'ArrayFilter',
      field_name: 'geometry_type',
      config: geometryTypes
    })
  }
}

const formSchema = toTypedSchema(z.object({
  projectIds: z.array(z.string()).default([]),
  templateIds: z.array(z.string()).default([]),
  memberIds: z.array(z.string()).default([]),
  searchText: z.string().default(''),
  isPublicData: z.boolean().optional().default(false),
  geometryTypes: z.array(z.string()).default([])
}))

const { handleSubmit, setValues, setFieldValue, values: formValues } = useForm({
  validationSchema: formSchema,
})

const emitFilters = () => {
  const projectIdsFilter = filterObject.value.filter.config.find(
    f => f.type === 'ArrayFilter' && f.field_name === 'project_id'
  )
  const projectIds = projectIdsFilter ? projectIdsFilter.config.map(id => parseInt(id)) : []

  const templateIdsFilter = filterObject.value.filter.config.find(
    f => f.type === 'ArrayFilter' && f.field_name === 'template_id'
  )
  const templateIds = templateIdsFilter ? templateIdsFilter.config.map(id => parseInt(id)) : []

  const memberIdsFilter = filterObject.value.filter.config.find(
    f => f.type === 'ArrayFilter' && f.field_name === 'member_id'
  )
  const memberIds = memberIdsFilter ? memberIdsFilter.config.map(id => parseInt(id)) : []

  const searchFilter = filterObject.value.filter.config.find(
    f => f.type === 'TextFilter' && f.field_name === 'search'
  )
  const searchText = searchFilter ? searchFilter.config : ''

  const publicDataFilter = filterObject.value.filter.config.find(
    f => f.type === 'BoolFilter' && f.field_name === 'is_public'
  )
  const isPublicData = publicDataFilter ? publicDataFilter.config : false

  const geometryTypesFilter = filterObject.value.filter.config.find(
    f => f.type === 'ArrayFilter' && f.field_name === 'geometry_type'
  )
  const geometryTypes = geometryTypesFilter ? geometryTypesFilter.config : []

  const filters = {
    projectIds,
    templateIds,
    memberIds,
    memberObjects: memberOptions.value.filter(m =>
      (memberIds || []).includes(m.member_id.toString())
    ),
    searchText,
    isPublicData,
    dateRange: { start: null, end: null },
    geometries: selectedGeometries.value,
    geometryTypes
  }

  emit('update:filters', filters)
}

watch(() => formValues, (newValues) => {
  if (newValues.projectIds) {
    addProjectFilter(newValues.projectIds)
  }

  if (newValues.templateIds) {
    addTemplateFilter(newValues.templateIds)
  }

  if (newValues.memberIds) {
    addMemberFilter(newValues.memberIds)
  }

  // searchText is handled by the debounced localSearchText watcher

  if (newValues.isPublicData !== undefined) {
    addPublicDataFilter(newValues.isPublicData)
  }

  if (newValues.dateRange && newValues.dateRange.start && newValues.dateRange.end) {
    addDateRangeFilter()
  }

  if (newValues.geometryTypes) {
    addGeometryTypeFilter(newValues.geometryTypes)
  }

  if (isInitialized.value) {
    emitFilters(false)
  }
}, { deep: true })

const handleSelectedGeometriesChange = (newGeometries) => {
  console.log('handleSelectedGeometriesChange', newGeometries)
  selectedGeometries.value = newGeometries
  addGeometryFilter(newGeometries)
  emitFilters(false)
}

watch(() => selectedGeometryTypes.value, (newGeometryTypes) => {
  addGeometryTypeFilter(newGeometryTypes)
  emitFilters(false)
})

const fetchTemplates = async (projectIds) => {
  if (!projectIds || projectIds.length === 0) {
    templateOptions.value = []
    return
  }

  isLoadingTemplates.value = true

  try {
    const templates = []

    for (const projectId of projectIds) {
      try {
        const response = await API({
          method: 'GET',
          url: `${HOST}/project/${projectId}/templates`
        })

        if (response.data && Array.isArray(response.data)) {
          const projectTemplates = response.data.map(template => ({
            ...template,
            project_id: projectId
          }))
          templates.push(...projectTemplates)
        }
      } catch (error) {
        console.error(`Failed to fetch templates for project ${projectId}:`, error)
      }
    }

    templateOptions.value = templates
  } catch (error) {
    console.error('Failed to fetch templates:', error)
    toast({
      title: 'Error',
      description: 'Failed to fetch report templates',
      variant: 'destructive',
    })
  } finally {
    isLoadingTemplates.value = false
  }
}

const fetchMembers = async (projectIds) => {
  if (!projectIds || projectIds.length === 0) {
    memberOptions.value = []
    return
  }

  isLoadingMembers.value = true

  try {
    const responses = await Promise.all(
      projectIds.map(id =>
        API({
          method: 'GET',
          url: `${HOST}/org/${props.orgId}/project/${id}/members`
        })
      )
    )

    const allMembers = responses.flatMap(r => r.data || [])
    const unique = Object.values(
      Object.fromEntries(allMembers.map(m => [m.member_id, m]))
    )
    memberOptions.value = unique
  } catch (error) {
    console.error('Failed to fetch members:', error)
    toast({
      title: 'Error',
      description: 'Failed to fetch project members',
      variant: 'destructive',
    })
    memberOptions.value = []
  } finally {
    isLoadingMembers.value = false
  }
}

const handleProjectChange = async (newValue) => {
  const currentTemplateIds = formValues.templateIds || []
  const currentMemberIds = formValues.memberIds || []

  addProjectFilter(newValue)

  await Promise.all([
    fetchTemplates(newValue),
    fetchMembers(newValue)
  ])

  const validTemplateIds = templateOptions.value
    .map(t => (t.id || t.template_id).toString())

  const filteredTemplateIds = currentTemplateIds.filter(id =>
    validTemplateIds.includes(id)
  )

  const validMemberIds = memberOptions.value
    .map(m => m.member_id.toString())

  const filteredMemberIds = currentMemberIds.filter(id =>
    validMemberIds.includes(id)
  )

  await setValues({
    ...formValues,
    projectIds: newValue,
    templateIds: filteredTemplateIds,
    memberIds: filteredMemberIds
  })

  addTemplateFilter(filteredTemplateIds)
  addMemberFilter(filteredMemberIds)

  emitFilters(false)
}

const handleTemplateChange = async (newValue) => {
  await setValues({
    ...formValues,
    templateIds: newValue || []
  })

  addTemplateFilter(newValue || [])
  emitFilters(false)
}

const handleMemberChange = async (newValue) => {
  await setValues({
    ...formValues,
    memberIds: newValue || []
  })

  addMemberFilter(newValue || [])
  emitFilters(false)
}

watch(localSearchText, (newValue) => {
  if (searchDebounceTimer) clearTimeout(searchDebounceTimer)
  searchDebounceTimer = setTimeout(() => {
    addSearchFilter(newValue)
    setValues({
      ...formValues,
      searchText: newValue
    })
  }, 400)
})

onUnmounted(() => {
  if (searchDebounceTimer) clearTimeout(searchDebounceTimer)
})

const handlePublicDataChange = (newValue) => {
  const boolValue = newValue === true

  setValues({
    ...formValues,
    isPublicData: boolValue
  })

  addPublicDataFilter(boolValue)
  emitFilters(false)
}

const parseInitialFilter = () => {
  if (props.initialFilter && props.initialFilter.filter) {
    filterObject.value = { ...props.initialFilter }
  }

  const filterConfig = filterObject.value.filter.config || []

  const projectIdsFilter = filterConfig.find(
    f => f.type === 'ArrayFilter' && f.field_name === 'project_id'
  )
  const projectIds = projectIdsFilter ? projectIdsFilter.config.map(id => id.toString()) : []

  const templateIdsFilter = filterConfig.find(
    f => f.type === 'ArrayFilter' && f.field_name === 'template_id'
  )
  const templateIds = templateIdsFilter ? templateIdsFilter.config.map(id => id.toString()) : []

  const memberIdsFilter = filterConfig.find(
    f => f.type === 'ArrayFilter' && f.field_name === 'member_id'
  )
  const memberIds = memberIdsFilter ? memberIdsFilter.config.map(id => id.toString()) : []

  const searchFilter = filterConfig.find(
    f => f.type === 'TextFilter' && f.field_name === 'search'
  )
  const searchText = searchFilter ? searchFilter.config : ''

  const publicDataFilter = filterConfig.find(
    f => f.type === 'BoolFilter' && f.field_name === 'is_public'
  )
  const isPublicData = publicDataFilter !== undefined ? publicDataFilter.config : undefined

  const dateRangeFilter = filterConfig.find(
    f => f.type === 'DateRangeFilter' && f.field_name === 'acquired'
  )
  if (dateRangeFilter) {
    filterObject.value.filter.config = filterObject.value.filter.config.filter(
      f => !(f.type === 'DateRangeFilter' && f.field_name === 'acquired')
    )
  }

  const geometryTypesFilter = filterConfig.find(
    f => f.type === 'ArrayFilter' && f.field_name === 'geometry_type'
  )
  const geometryTypes = geometryTypesFilter ? geometryTypesFilter.config : []
  selectedGeometryTypes.value = geometryTypes

  const geometryFilter = filterConfig.find(
    f => f.type === 'GeometryFilter' && f.field_name === 'geometry'
  )
  if (geometryFilter && geometryFilter.config) {
    const config = geometryFilter.config
    if (config.type === 'MultiPolygon') {
      // Convert back to array of features for display
      selectedGeometries.value = config.coordinates.map((coords, i) => ({
        type: 'Feature',
        id: `restored-${i}`,
        geometry: { type: 'Polygon', coordinates: coords },
        properties: { name: `Area ${i + 1}` }
      }))
    } else if (config.type === 'Polygon') {
      selectedGeometries.value = [{
        type: 'Feature',
        id: 'restored-0',
        geometry: config,
        properties: { name: 'Selected Area' }
      }]
    }
  }

  return {
    projectIds,
    templateIds,
    memberIds,
    searchText,
    isPublicData,
    dateRange: { start: null, end: null },
    geometryTypes
  }
}

const loadInitialData = async () => {
  const initialValues = parseInitialFilter();

  // Use projectId prop as fallback when no project IDs from filter config
  const projectIdsForLoading = initialValues.projectIds.length > 0
    ? initialValues.projectIds
    : (props.projectId ? [props.projectId.toString()] : [])

  if (projectIdsForLoading.length > 0) {
    await Promise.all([
      fetchTemplates(projectIdsForLoading),
      fetchMembers(projectIdsForLoading)
    ]);
  }

  const values = {
    projectIds: initialValues.projectIds || [],
    templateIds: initialValues.templateIds || [],
    memberIds: initialValues.memberIds || [],
    searchText: initialValues.searchText || '',
    isPublicData: initialValues.isPublicData,
    geometryTypes: initialValues.geometryTypes || []
  };

  await setValues(values);

  if (initialValues.searchText) {
    setFieldValue('searchText', initialValues.searchText);
  }

  if (initialValues.isPublicData !== undefined) {
    setFieldValue('isPublicData', initialValues.isPublicData);
    addPublicDataFilter(initialValues.isPublicData);
  }

}

onMounted(async () => {
  await fetchOrgProjects()
  await loadInitialData()
  localSearchText.value = formValues.searchText || ''
  nextTick(() => {
    isInitialized.value = true
    emitFilters(false)
  })
})

const getTemplateName = (templateId) => {
  const template = templateOptions.value.find(t => t.id?.toString() === templateId.toString() ||
                                                 t.template_id?.toString() === templateId.toString())
  return template ? template.name || template.template_name : `Template ${templateId}`
}

const getTemplateColor = (templateId) => {
  const template = templateOptions.value.find(t => t.id?.toString() === templateId.toString() ||
                                                 t.template_id?.toString() === templateId.toString())
  return template && template.color ? template.color : '#888888'
}

const getTemplateIcon = (templateId) => {
  const template = templateOptions.value.find(t => t.id?.toString() === templateId.toString() ||
                                                 t.template_id?.toString() === templateId.toString())
  return template && template.s3_icon ? template.s3_icon : null
}

const getMember = (memberId) => {
  return memberOptions.value.find(m => m.member_id.toString() === memberId.toString())
}

const getMemberName = (memberId) => {
  const member = getMember(memberId)
  return member ? (member.display_name || member.email || member.username || `Member ${memberId}`) : `Member ${memberId}`
}

const getProjectName = (projectId) => {
  const project = projects.value.find(p => p.project_id.toString() === projectId.toString())
  return project ? project.project_name : `Project ${projectId}`
}

const getGeometryTypeName = (typeId) => {
  const type = geometryTypeOptions.find(t => t.id === typeId)
  return type ? type.name : typeId
}
</script>

<template>
  <div>
    <Form
      :validation-schema="formSchema"
      @submit="emitFilters"
      class="space-y-3"
    >
      <FormField v-if="!hideProjectSelector" v-slot="{ field, componentField }" name="projectIds">
        <FormItem>
          <FormLabel>Project <span class="text-gray-500">(optional)</span></FormLabel>
          <FormControl>
            <ProjectSelect
              :projects="projects"
              :multiple="true"
              :model-value="formValues.projectIds"
              :disabled="props.disabled"
              placeholder="Select projects"
              @update:model-value="handleProjectChange"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <FormField v-slot="{ field, componentField }" name="searchText">
        <FormItem>
          <FormControl>
            <Input
              v-model="localSearchText"
              placeholder="Search by text"
              :disabled="props.disabled"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <FormField v-slot="{ field }" name="isPublicData">
        <FormItem class="flex flex-row items-center space-x-2 space-y-0">
          <FormControl>
            <Switch
              :checked="field.value === true"
              @update:modelValue="handlePublicDataChange"
              :disabled="props.disabled"
            />
          </FormControl>
          <FormLabel class="cursor-pointer">Only Public</FormLabel>
          <FormMessage />
        </FormItem>
      </FormField>

      <FormField v-slot="{ field, componentField }" name="geometryTypes">
        <FormItem>
          <FormLabel>Geometry Type</FormLabel>
          <FormControl>
            <Select multiple v-model="selectedGeometryTypes">
              <SelectTrigger class="h-auto min-h-[40px] flex-wrap">
                <SelectValue placeholder="Any">
                  <div v-if="selectedGeometryTypes && selectedGeometryTypes.length > 0" class="flex flex-wrap gap-1">
                    <div v-for="typeId in selectedGeometryTypes" :key="typeId"
                         class="bg-primary/10 text-primary px-1.5 py-0.5 rounded-md text-sm flex items-center gap-1">
                      {{ getGeometryTypeName(typeId) }}
                    </div>
                  </div>
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem
                    v-for="type in geometryTypeOptions"
                    :key="type.id"
                    :value="type.id"
                  >
                    {{ type.name }}
                  </SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <FormField v-slot="{ field, componentField }" name="templateIds">
        <FormItem>
          <FormLabel>Templates</FormLabel>
          <FormControl>
            <div>
              <Select v-bind="componentField" multiple
                    :modelValue="formValues.templateIds" @update:modelValue="handleTemplateChange"
                    :disabled="isLoadingTemplates || templateOptions.length === 0">
                <SelectTrigger class="h-auto min-h-[40px] flex-wrap">
                  <SelectValue placeholder="Any">
                    <div v-if="isLoadingTemplates" class="flex items-center gap-2">
                      <Spinner class="w-4 h-4" />
                      <span>Loading templates...</span>
                    </div>
                    <div v-else-if="formValues.templateIds && formValues.templateIds.length > 0" class="flex flex-wrap gap-1">
                      <div v-for="templateId in formValues.templateIds" :key="templateId"
                          class="px-1.5 py-0.5 rounded-md text-sm flex items-center gap-1.5"
                          :style="{
                            backgroundColor: getTemplateColor(templateId) + '33',
                            color: getTemplateColor(templateId),
                            borderColor: getTemplateColor(templateId)
                          }">
                        <img v-if="getTemplateIcon(templateId)"
                             :src="getTemplateIcon(templateId)"
                             class="w-4 h-4 rounded-sm object-cover" />
                        {{ getTemplateName(templateId) }}
                      </div>
                    </div>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <div v-if="isLoadingTemplates" class="p-2 text-center">
                    <Spinner class="mx-auto w-5 h-5" />
                    <p class="text-sm text-muted-foreground mt-2">Loading templates...</p>
                  </div>
                  <div v-else-if="templateOptions.length === 0" class="p-2 text-center text-muted-foreground">
                    No templates available
                  </div>
                  <SelectGroup v-else>
                    <SelectItem
                      v-for="template in templateOptions"
                      :key="template.id || template.template_id"
                      :value="(template.id || template.template_id).toString()"
                    >
                      <div class="flex items-center gap-2">
                        <div class="w-3 h-3 rounded-full"
                            :style="{ backgroundColor: template.color || '#888888' }"></div>
                        <img v-if="template.s3_icon"
                            :src="template.s3_icon"
                            class="w-4 h-4 rounded-sm object-cover" />
                        {{ template.name || template.template_name }}
                      </div>
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <FormField v-slot="{ field, componentField }" name="memberIds">
        <FormItem>
          <FormLabel>Members</FormLabel>
          <FormControl>
            <div>
              <Select v-bind="componentField" multiple
                    :modelValue="formValues.memberIds" @update:modelValue="handleMemberChange"
                    :disabled="isLoadingMembers || memberOptions.length === 0">
                <SelectTrigger class="h-auto min-h-[40px] flex-wrap">
                  <SelectValue placeholder="Any">
                    <div v-if="isLoadingMembers" class="flex items-center gap-2">
                      <Spinner class="w-4 h-4" />
                      <span>Loading members...</span>
                    </div>
                    <div v-else-if="formValues.memberIds && formValues.memberIds.length > 0" class="flex flex-wrap gap-1">
                      <div v-for="memberId in formValues.memberIds" :key="memberId"
                          class="bg-primary/10 text-primary px-1.5 py-0.5 rounded-md text-sm flex items-center gap-1.5">
                        <Avatar v-if="getMember(memberId)" class="w-5 h-5 shrink-0">
                          <AvatarImage v-if="getMember(memberId)?.avatar_url" :src="getMember(memberId)?.avatar_url" />
                          <AvatarFallback />
                        </Avatar>
                        <span>{{ getMemberName(memberId) }}</span>
                        <VerifiedMarker v-if="getMember(memberId)?.verified" class="h-3 w-3" />
                      </div>
                    </div>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <div v-if="isLoadingMembers" class="p-2 text-center">
                    <Spinner class="mx-auto w-5 h-5" />
                    <p class="text-sm text-muted-foreground mt-2">Loading members...</p>
                  </div>
                  <div v-else-if="memberOptions.length === 0" class="p-2 text-center text-muted-foreground">
                    No members available
                  </div>
                  <SelectGroup v-else>
                    <SelectItem
                      v-for="member in memberOptions"
                      :key="member.member_id"
                      :value="member.member_id.toString()"
                    >
                      <div class="flex items-center gap-2">
                        <Avatar class="w-6 h-6 shrink-0">
                          <AvatarImage v-if="member.avatar_url" :src="member.avatar_url" />
                          <AvatarFallback />
                        </Avatar>
                        <span>{{ member.display_name || member.email || member.username || 'Unknown' }}</span>
                        <VerifiedMarker v-if="member.verified" class="h-3.5 w-3.5" />
                      </div>
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <FormField v-slot="{ field }" name="geometry">
        <FormItem>
          <FormLabel>AOI (Area of Interest)</FormLabel>
          <FormControl>
            <FeatureSelection
              :selectedGeometries="selectedGeometries"
              :multiSelect="true"
              :allowed-draw-modes="['polygon']"
              @update:selectedGeometries="handleSelectedGeometriesChange"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </Form>

    <!-- <div class="mt-4" v-if="!props.disabled">
      Debug
      <pre class="text-xs p-2 bg-gray-100 rounded-md overflow-auto max-h-[300px]">{{ JSON.stringify(filterObject, null, 2) }}</pre>
    </div> -->
  </div>
</template>
